// React Context для состояния калькулятора

import { createContext, useContext, useReducer, type ReactNode, useCallback } from 'react';
import type { 
  CalculationInput, 
  VoltageLossResult, 
  HeatingResult, 
  CalculatorState, 
  CalculatorAction 
} from '../types';
import { NOMINAL_VOLTAGES, DEFAULT_POWER_FACTOR, BASE_AMBIENT_TEMP } from '../data/constants';

// Начальное состояние ввода
const initialInput: CalculationInput = {
  nominalVoltage: NOMINAL_VOLTAGES.threePhase,
  current: 0,
  power: null,
  length: 100,
  crossSection: 2.5,
  powerFactor: DEFAULT_POWER_FACTOR.power,
  networkType: 'three-phase',
  material: 'copper',
  loadType: 'power',
  installationMethod: 'air',
  ambientTemp: BASE_AMBIENT_TEMP.air,
  cablesCount: 1,
  lineType: 'cable',
  insulationType: 'pvc',
};

// Начальное состояние калькулятора
const initialState: CalculatorState = {
  input: initialInput,
  voltageLossResult: null,
  heatingResult: null,
  activeTab: 'voltage',
};

// Редьюсер для управления состоянием
function calculatorReducer(state: CalculatorState, action: CalculatorAction): CalculatorState {
  switch (action.type) {
    case 'SET_INPUT':
      return {
        ...state,
        input: {
          ...state.input,
          ...action.payload,
        },
      };
    case 'SET_VOLTAGE_LOSS_RESULT':
      return {
        ...state,
        voltageLossResult: action.payload,
      };
    case 'SET_HEATING_RESULT':
      return {
        ...state,
        heatingResult: action.payload,
      };
    case 'SET_ACTIVE_TAB':
      return {
        ...state,
        activeTab: action.payload,
      };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

// Тип контекста
interface CalculatorContextType {
  state: CalculatorState;
  input: CalculationInput;
  voltageLossResult: VoltageLossResult | null;
  heatingResult: HeatingResult | null;
  activeTab: 'voltage' | 'heating';
  setInput: (input: Partial<CalculationInput>) => void;
  setVoltageLossResult: (result: VoltageLossResult | null) => void;
  setHeatingResult: (result: HeatingResult | null) => void;
  setActiveTab: (tab: 'voltage' | 'heating') => void;
  reset: () => void;
}

// Создание контекста
const CalculatorContext = createContext<CalculatorContextType | undefined>(undefined);

// Провайдер
interface CalculatorProviderProps {
  children: ReactNode;
}

export function CalculatorProvider({ children }: CalculatorProviderProps) {
  const [state, dispatch] = useReducer(calculatorReducer, initialState);

  const setInput = useCallback((input: Partial<CalculationInput>) => {
    dispatch({ type: 'SET_INPUT', payload: input });
  }, []);

  const setVoltageLossResult = useCallback((result: VoltageLossResult | null) => {
    dispatch({ type: 'SET_VOLTAGE_LOSS_RESULT', payload: result });
  }, []);

  const setHeatingResult = useCallback((result: HeatingResult | null) => {
    dispatch({ type: 'SET_HEATING_RESULT', payload: result });
  }, []);

  const setActiveTab = useCallback((tab: 'voltage' | 'heating') => {
    dispatch({ type: 'SET_ACTIVE_TAB', payload: tab });
  }, []);

  const reset = useCallback(() => {
    dispatch({ type: 'RESET' });
  }, []);

  const value: CalculatorContextType = {
    state,
    input: state.input,
    voltageLossResult: state.voltageLossResult,
    heatingResult: state.heatingResult,
    activeTab: state.activeTab,
    setInput,
    setVoltageLossResult,
    setHeatingResult,
    setActiveTab,
    reset,
  };

  return (
    <CalculatorContext.Provider value={value}>
      {children}
    </CalculatorContext.Provider>
  );
}

// Хук для использования контекста
// eslint-disable-next-line react-refresh/only-export-components
export function useCalculator() {
  const context = useContext(CalculatorContext);
  if (context === undefined) {
    throw new Error('useCalculator must be used within a CalculatorProvider');
  }
  return context;
}

// Экспорт начального состояния для использования в тестах
// eslint-disable-next-line react-refresh/only-export-components
export { initialState, initialInput };
